import { ISubscriber } from '@studyportals/event-aggregation-service-interface';

import { StudentRepository } from '../../domain/student-repository';
import {
	EventType,
	AnonymousStudentProfileUpdated,
	AnonymousStudentServiceReady,
	AnonymousStudentProfileSynced,
} from '@studyportals/anonymous-student-interfaces';
import { StudentField } from '@studyportals/studentdomain';
import { IStore } from '@/interfaces/store.interfaces';
import globals from '@/utils/globals';
import { StudentClient } from '@/infrastructure/student-client';

export class ProfileDataCheckerPlugin
implements ISubscriber<AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced>
{
	private studentRepository: StudentRepository;
	private store: IStore;

	constructor(store: IStore) {
		this.store = store;
		this.studentRepository = this.newStudentRepository;
		globals.studentRepository = this.studentRepository;
	}

	get newStudentRepository(): StudentRepository {
		return new StudentRepository(new StudentClient());
	}

	get eventAggregationService(): any {
		globals.eventAggregationService = window['EventAggregationService'];
		return window['EventAggregationService'];
	}

	public async notify(
		event: AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced,
	): Promise<void> {
		if (
			event.eventType === AnonymousStudentServiceReady.EventType ||
			event.eventType === AnonymousStudentProfileSynced.EventType
		) {
			await this.loadInitialData();
		}

		if (event.eventType === AnonymousStudentProfileUpdated.EventType) {
			await this.handleStudentProfileUpdated(event as AnonymousStudentProfileUpdated);
		}
	}

	public initiate(): void {
		globals.eventAggregationService.subscribeTo(EventType.AnonymousStudentServiceReady, this, true);
		globals.eventAggregationService.subscribeTo(EventType.AnonymousStudentProfileUpdated, this);
		globals.eventAggregationService.subscribeTo(EventType.AnonymousStudentProfileSynced, this);
	}

	public async loadInitialData(): Promise<void> {
		const dataFields = [
			StudentField.GENDER,
			StudentField.FIRST_NAME,
			StudentField.EMAIL,
			StudentField.INTERESTS_COUNTRIES,
			StudentField.INTERESTS_DISCIPLINES,
			StudentField.NATIONALITY_COUNTRY_ISO,
			StudentField.RESIDENCE_COUNTRY_ID,
			StudentField.START_PERIOD_DATE,
			StudentField.JOURNEY_STATE,
			StudentField.EMAILING_FAVOURITES,
			StudentField.EMAILING_OTHER,
			StudentField.EMAILING_SCHOLARSHIPS,
			StudentField.EMAILING_UPDATES,
		];

		const studentData = await this.studentRepository.getFields(dataFields);

		if (studentData === null) {
			return;
		}

		await this.updateProfileCompleteness();
		this.store.actions.setGender(studentData[StudentField.GENDER] ?? '');
		this.store.actions.setName(studentData[StudentField.FIRST_NAME] ?? '');
		this.store.mutations.setEmail(studentData[StudentField.EMAIL] ?? '');
		this.store.actions.setCountriesOfInterest(studentData[StudentField.INTERESTS_COUNTRIES] ?? []);
		this.store.actions.setAttendance(studentData[StudentField.ATTENDANCE] ?? []);
		this.store.actions.setDisciplinesOfInterest(studentData[StudentField.INTERESTS_DISCIPLINES] ?? []);
		this.store.mutations.setNationalityCountry(studentData[StudentField.NATIONALITY_COUNTRY_ISO] ?? '');
		this.store.mutations.setResidenceCountry(studentData[StudentField.RESIDENCE_COUNTRY_ID]?.toString() ?? '');
		this.store.mutations.setStartDate(studentData[StudentField.START_PERIOD_DATE] ?? '');
		this.store.actions.setJourneyState(studentData[StudentField.JOURNEY_STATE] ?? {});
		this.store.mutations.setEmailingFavourites(studentData[StudentField.EMAILING_FAVOURITES] ?? false);
		this.store.mutations.setEmailingOther(studentData[StudentField.EMAILING_OTHER] ?? false);
		this.store.mutations.setEmailingScholarships(studentData[StudentField.EMAILING_SCHOLARSHIPS] ?? false);
		this.store.mutations.setEmailingUpdates(studentData[StudentField.EMAILING_UPDATES] ?? false);
		this.store.actions.setIsInitialStudentDataLoaded(true);
	}

	private async handleStudentProfileUpdated(event: AnonymousStudentProfileUpdated): Promise<void> {
		await this.updateProfileCompleteness();

		if (Object.keys(event.changes).indexOf(StudentField.GENDER) > -1) {
			this.store.actions.setGender(event.changes[StudentField.GENDER] ?? '');
		}

		if (Object.keys(event.changes).indexOf(StudentField.FIRST_NAME) > -1) {
			this.store.actions.setName(event.changes[StudentField.FIRST_NAME] ?? '');
		}

		if (Object.keys(event.changes).indexOf(StudentField.INTERESTS_COUNTRIES) > -1) {
			const updatedData = await this.studentRepository.getFields([StudentField.INTERESTS_COUNTRIES]);
			if (updatedData !== null) {
				this.store.actions.setCountriesOfInterest(updatedData[StudentField.INTERESTS_COUNTRIES] ?? []);
			}
		}

		if (Object.keys(event.changes).indexOf(StudentField.ATTENDANCE) > -1) {
			const updatedData = await this.studentRepository.getFields([StudentField.ATTENDANCE]);
			if (updatedData !== null) {
				this.store.actions.setAttendance(updatedData[StudentField.ATTENDANCE] ?? []);
			}
		}

		if (Object.keys(event.changes).indexOf(StudentField.INTERESTS_DISCIPLINES) > -1) {
			const updatedData = await this.studentRepository.getFields([StudentField.INTERESTS_DISCIPLINES]);
			if (updatedData !== null) {
				this.store.actions.setDisciplinesOfInterest(updatedData[StudentField.INTERESTS_DISCIPLINES] ?? []);
			}
		}

		if (Object.keys(event.changes).indexOf(StudentField.NATIONALITY_COUNTRY_ISO) > -1) {
			this.store.mutations.setNationalityCountry(event.changes[StudentField.NATIONALITY_COUNTRY_ISO] ?? '');
		}

		if (Object.keys(event.changes).indexOf(StudentField.RESIDENCE_COUNTRY_ID) > -1) {
			this.store.mutations.setResidenceCountry(event.changes[StudentField.RESIDENCE_COUNTRY_ID]?.toString() ?? '');
		}

		if (Object.keys(event.changes).indexOf(StudentField.START_PERIOD_DATE) > -1) {
			this.store.mutations.setStartDate(event.changes[StudentField.START_PERIOD_DATE] ?? '');
		}

		if (Object.keys(event.changes).indexOf(StudentField.JOURNEY_STATE) > -1) {
			this.store.actions.setJourneyState(event.changes[StudentField.JOURNEY_STATE] ?? {});
		}

		if (Object.keys(event.changes).indexOf(StudentField.EMAILING_FAVOURITES) > -1) {
			this.store.mutations.setEmailingFavourites(event.changes[StudentField.EMAILING_FAVOURITES] ?? false);
		}

		if (Object.keys(event.changes).indexOf(StudentField.EMAILING_OTHER) > -1) {
			this.store.mutations.setEmailingOther(event.changes[StudentField.EMAILING_OTHER] ?? false);
		}

		if (Object.keys(event.changes).indexOf(StudentField.EMAILING_SCHOLARSHIPS) > -1) {
			this.store.mutations.setEmailingScholarships(event.changes[StudentField.EMAILING_SCHOLARSHIPS] ?? false);
		}

		if (Object.keys(event.changes).indexOf(StudentField.EMAILING_UPDATES) > -1) {
			this.store.mutations.setEmailingUpdates(event.changes[StudentField.EMAILING_UPDATES] ?? false);
		}
	}

	private async updateProfileCompleteness(): Promise<void> {
		const profileCompleteness = await this.studentRepository.getCompleteness();
		this.store.actions.setProfileCompleteness(profileCompleteness);
	}
}
